import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API, BACKEND_URL } from '../config/api';
import { HORSE_TYPES, GENDERS, GALOPPO_LEVELS, typeLabel, normalizeGender } from '../config/horseOptions';

// Image Upload Component
const ImageUploader = ({ value, onChange, label, accept = "image/jpeg,image/png,image/webp", multiple = false, testId = "image-uploader" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        setPreviews(value.filter(Boolean));
      } else if (typeof value === 'string' && value.trim()) {
        setPreviews([value]);
      }
    } else {
      setPreviews([]);
    }
  }, [value]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(`${BACKEND_URL}${res.data.url}`);
      }
      if (multiple) {
        const combined = [...(Array.isArray(value) ? value : []), ...uploaded];
        onChange(combined);
      } else {
        onChange(uploaded[0]);
      }
    } catch (err) {
      alert('Errore upload: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = (idx) => {
    if (multiple && Array.isArray(value)) {
      const updated = value.filter((_, i) => i !== idx);
      onChange(updated);
    } else {
      onChange('');
    }
  };

  return (
    <div data-testid={testId}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {previews.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {previews.map((url, idx) => (
            <div key={idx} className="relative group">
              <img src={url} alt="" className="w-20 h-20 object-cover rounded border border-gray-200" />
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-[#c9a227] hover:text-[#c9a227] transition-colors cursor-pointer disabled:opacity-50"
          data-testid={`${testId}-btn`}
        >
          {uploading ? 'Caricamento...' : (multiple ? 'Carica immagini' : 'Carica immagine')}
        </button>
        {uploading && <div className="w-4 h-4 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin"></div>}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
};

// Document Upload Component  
const DocUploader = ({ docs, setDocs }) => {
  const fileRef = useRef(null);
  const [uploadIdx, setUploadIdx] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || uploadIdx === null) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const updated = [...docs];
      updated[uploadIdx].url = `${BACKEND_URL}${res.data.url}`;
      setDocs(updated);
    } catch (err) {
      alert('Errore upload: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      setUploadIdx(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-[#64748b] uppercase tracking-wider">Documenti (PDF, Certificati)</p>
        <button type="button" onClick={() => setDocs([...docs, {name: '', url: ''}])} className="text-sm text-[#c9a227] font-medium hover:underline">
          + Aggiungi documento
        </button>
      </div>
      {docs.map((doc, idx) => (
        <div key={idx} className="flex items-center gap-2 mt-2">
          <input type="text" placeholder="Nome (es: Certificato Vet)" value={doc.name} onChange={e => { const d = [...docs]; d[idx].name = e.target.value; setDocs(d); }} className="input-field text-sm flex-1" />
          {doc.url ? (
            <span className="text-xs text-green-600 font-medium px-2 truncate max-w-[150px]" title={doc.url}>Caricato</span>
          ) : (
            <button 
              type="button" 
              onClick={() => { setUploadIdx(idx); fileRef.current?.click(); }}
              disabled={uploading}
              className="px-3 py-1.5 border border-dashed border-gray-300 text-xs text-gray-600 hover:border-[#c9a227] hover:text-[#c9a227] rounded transition-colors whitespace-nowrap"
            >
              {uploading && uploadIdx === idx ? 'Caricamento...' : 'Carica PDF'}
            </button>
          )}
          <button type="button" onClick={() => setDocs(docs.filter((_, i) => i !== idx))} className="text-red-500 px-2 hover:text-red-700 text-lg">x</button>
        </div>
      ))}
      <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={handleUpload} className="hidden" />
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [horses, setHorses] = useState([]);
  const [banners, setBanners] = useState([]);
  const [news, setNews] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [auctionResults, setAuctionResults] = useState([]);
  const [resultArticles, setResultArticles] = useState([]);
  const [endedAuctionDates, setEndedAuctionDates] = useState([]);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    firstClosing: '',
    intervalMinutes: 2,
    selectedAuctions: []
  });
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showHorseModal, setShowHorseModal] = useState(false);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [showBulkAuctionModal, setShowBulkAuctionModal] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);

  // Form states
  const [horseForm, setHorseForm] = useState({
    name: '', breed: '', age: '', gender: 'Maschio', horse_type: 'fattrice', galoppo_level: '', color: '', height_cm: '', birth_year: '', description: '', images: [], location: '',
    father: '', mother: '', grandfather: '', registration_number: '',
    pregnant_with: '', expected_foaling: '',
    seller_name: '', seller_contact: '', seller_email: '', seller_phone: '',
    external_link: '', external_link_label: ''
  });
  const [horseDocs, setHorseDocs] = useState([]);
  const [auctionForm, setAuctionForm] = useState({
    horse_id: '', title: '', starting_price: 500, start_time: '', end_time: ''
  });
  const [bannerForm, setBannerForm] = useState({
    title: '', image_url: '', link_url: '', position: 'header', is_active: true
  });
  const [newsForm, setNewsForm] = useState({
    title: '', content: '', excerpt: '', image_url: '', author: 'Redazione Equus Prime', is_published: true
  });
  const [tableRows, setTableRows] = useState([]);
  const [announcementForm, setAnnouncementForm] = useState({
    text: '', time_text: 'alle ore 19:15.', order: 0
  });
  const [resultForm, setResultForm] = useState({
    title: '', date_text: '', image_url: '', link_url: '/risultati', order: 0
  });
  const [articleForm, setArticleForm] = useState({
    date_key: '', title: '', content: '', body: '', image_url: '', author: 'Equus Prime'
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, auctionsRes, horsesRes, bannersRes, newsRes, annRes, resultsRes, articlesRes, maintenanceRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/auctions`),
        axios.get(`${API}/horses`),
        axios.get(`${API}/admin/banners`),
        axios.get(`${API}/news?limit=50`),
        axios.get(`${API}/admin/announcements`),
        axios.get(`${API}/admin/auction-results`),
        axios.get(`${API}/admin/result-articles`),
        axios.get(`${API}/maintenance`).catch(() => ({ data: { maintenance: false } }))
      ]);
      setUsers(usersRes.data);
      setAuctions(auctionsRes.data);
      setHorses(horsesRes.data);
      setBanners(bannersRes.data);
      setNews(newsRes.data);
      setAnnouncements(annRes.data);
      setAuctionResults(resultsRes.data);
      setResultArticles(articlesRes.data);
      setMaintenanceMode(maintenanceRes.data.maintenance);
      
      // Compute ended auction dates
      const ended = auctionsRes.data.filter(a => a.status === 'ended');
      const dateMap = {};
      ended.forEach(a => {
        const dk = new Date(a.end_time).toISOString().split('T')[0];
        if (!dateMap[dk]) dateMap[dk] = { count: 0 };
        dateMap[dk].count++;
      });
      setEndedAuctionDates(Object.entries(dateMap).sort((a, b) => b[0].localeCompare(a[0])).map(([dk, info]) => ({ date_key: dk, count: info.count })));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // User actions
  const toggleVerify = async (userId, isVerified) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/${isVerified ? 'unverify' : 'verify'}`);
      window.dispatchEvent(new Event('pending-users-changed'));
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'operazione');
    }
  };

  const updateUserFlag = async (userId, action, value) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/${action}?${action === 'suspend' ? 'suspended' : 'is_admin'}=${value}`);
      window.dispatchEvent(new Event('pending-users-changed'));
      fetchAllData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore durante l\'operazione');
    }
  };

  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [pendingOnly, setPendingOnly] = useState(false);
  const isPending = (u) => !u.is_verified && !u.is_admin && !u.is_suspended;
  const pendingUsers = users.filter(isPending);
  const pendingAziende = pendingUsers.filter(u => u.account_type === 'azienda').length;
  const visibleUsers = users.filter(u =>
    (userTypeFilter === 'all' || (u.account_type === 'azienda') === (userTypeFilter === 'azienda')) && (!pendingOnly || isPending(u))
  );

  const canManage = (u) => u.user_id !== user.user_id && !u.is_super_admin && (!u.is_admin || user.is_super_admin);

  // Horse actions
  const createHorse = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...horseForm,
        age: parseInt(horseForm.age),
        height_cm: horseForm.height_cm ? parseInt(horseForm.height_cm) : null,
        birth_year: horseForm.birth_year ? parseInt(horseForm.birth_year) : null,
        images: Array.isArray(horseForm.images) ? horseForm.images.filter(Boolean) : [],
        horse_type: horseForm.horse_type,
        documents: horseDocs.filter(d => d.name.trim()),
        father: horseForm.father || null,
        mother: horseForm.mother || null,
        grandfather: horseForm.grandfather || null,
        registration_number: horseForm.registration_number || null,
        pregnant_with: horseForm.pregnant_with || null,
        expected_foaling: horseForm.expected_foaling || null,
        seller_name: horseForm.seller_name || null,
        seller_contact: horseForm.seller_contact || null,
        seller_email: horseForm.seller_email || null,
        seller_phone: horseForm.seller_phone || null,
        external_link: horseForm.external_link || null,
        external_link_label: horseForm.external_link_label || null,
        galoppo_level: horseForm.galoppo_level || null
      };
      await axios.post(`${API}/admin/horses`, data);
      setShowHorseModal(false);
      setHorseForm({ name: '', breed: '', age: '', gender: 'Maschio', horse_type: 'fattrice', galoppo_level: '', color: '', height_cm: '', birth_year: '', description: '', images: [], location: '', father: '', mother: '', grandfather: '', registration_number: '', pregnant_with: '', expected_foaling: '', seller_name: '', seller_contact: '', seller_email: '', seller_phone: '', external_link: '', external_link_label: '' });
      setHorseDocs([]);
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const deleteHorse = async (horseId) => {
    if (!window.confirm('Eliminare questo cavallo?')) return;
    try {
      await axios.delete(`${API}/admin/horses/${horseId}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  // Auction actions
  const createAuction = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...auctionForm,
        starting_price: parseFloat(auctionForm.starting_price)
      };
      await axios.post(`${API}/admin/auctions`, data);
      setShowAuctionModal(false);
      setAuctionForm({ horse_id: '', title: '', starting_price: 500, start_time: '', end_time: '' });
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  // Bulk auction state
  const [bulkSelectedHorses, setBulkSelectedHorses] = useState([]);
  const [bulkForm, setBulkForm] = useState({ starting_price: 500, start_time: '', end_time: '' });
  const [bulkCreating, setBulkCreating] = useState(false);

  const toggleBulkHorse = (horseId) => {
    setBulkSelectedHorses(prev => 
      prev.includes(horseId) ? prev.filter(id => id !== horseId) : [...prev, horseId]
    );
  };

  const toggleAllHorses = () => {
    if (bulkSelectedHorses.length === horses.length) {
      setBulkSelectedHorses([]);
    } else {
      setBulkSelectedHorses(horses.map(h => h.horse_id));
    }
  };

  const createBulkAuctions = async (e) => {
    e.preventDefault();
    if (bulkSelectedHorses.length === 0) { alert('Seleziona almeno un cavallo'); return; }
    setBulkCreating(true);
    try {
      // Send placeholder dates - will be overridden by "Programma Asta Serale"
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const res = await axios.post(`${API}/admin/auctions/bulk`, {
        horse_ids: bulkSelectedHorses,
        starting_price: parseFloat(bulkForm.starting_price),
        start_time: now.toISOString(),
        end_time: tomorrow.toISOString()
      });
      alert(res.data.message + '\nOra usa "Programma Asta Serale" per renderle visibili!');
      setShowBulkAuctionModal(false);
      setBulkSelectedHorses([]);
      setBulkForm({ starting_price: 500, start_time: '', end_time: '' });
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    } finally {
      setBulkCreating(false);
    }
  };

  const updateAuctionStatus = async (auctionId, status) => {
    try {
      await axios.put(`${API}/admin/auctions/${auctionId}?status=${status}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'aggiornamento');
    }
  };

  const deleteAuction = async (auctionId) => {
    if (!window.confirm('Eliminare questa asta?')) return;
    try {
      await axios.delete(`${API}/admin/auctions/${auctionId}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  // Banner actions
  const createBanner = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/banners`, bannerForm);
      setShowBannerModal(false);
      setBannerForm({ title: '', image_url: '', link_url: '', position: 'header', is_active: true });
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const deleteBanner = async (bannerId) => {
    if (!window.confirm('Eliminare questo banner?')) return;
    try {
      await axios.delete(`${API}/admin/banners/${bannerId}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  // News actions
  const createNews = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/news`, {
        ...newsForm,
        results_table: tableRows.filter(r => r.horse.trim())
      });
      setShowNewsModal(false);
      setNewsForm({ title: '', content: '', excerpt: '', image_url: '', author: 'Redazione Equus Prime', is_published: true });
      setTableRows([]);
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const addTableRow = () => {
    setTableRows([...tableRows, { horse: '', type: '', buyer: '', price: '' }]);
  };

  const updateTableRow = (idx, field, value) => {
    const updated = [...tableRows];
    updated[idx][field] = value;
    setTableRows(updated);
  };

  const removeTableRow = (idx) => {
    setTableRows(tableRows.filter((_, i) => i !== idx));
  };

  const deleteNews = async (newsId) => {
    if (!window.confirm('Eliminare questa notizia?')) return;
    try {
      await axios.delete(`${API}/admin/news/${newsId}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  // Announcement CRUD
  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/announcements`, {
        ...announcementForm,
        order: parseInt(announcementForm.order) || 0
      });
      setShowAnnouncementModal(false);
      setAnnouncementForm({ text: '', time_text: 'alle ore 19:15.', order: 0 });
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const deleteAnnouncement = async (annId) => {
    if (!window.confirm('Eliminare questo annuncio?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${annId}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  // Auction Results CRUD
  const handleResultSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/auction-results`, {
        ...resultForm,
        order: parseInt(resultForm.order) || 0
      });
      setShowResultModal(false);
      setResultForm({ title: '', date_text: '', image_url: '', link_url: '/risultati', order: 0 });
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const deleteResult = async (resultId) => {
    if (!window.confirm('Eliminare questo risultato?')) return;
    try {
      await axios.delete(`${API}/admin/auction-results/${resultId}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  // Result Article CRUD
  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/result-articles/${editingArticle.article_id}`, {
          title: articleForm.title,
          content: articleForm.content,
          body: articleForm.body,
          image_url: articleForm.image_url,
          author: articleForm.author
        });
      } else {
        await axios.post(`${API}/admin/result-articles`, articleForm);
      }
      setShowArticleModal(false);
      setEditingArticle(null);
      setArticleForm({ date_key: '', title: '', content: '', body: '', image_url: '', author: 'Equus Prime' });
      fetchAllData();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  const openEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      date_key: article.date_key,
      title: article.title,
      content: article.content,
      body: article.body || '',
      image_url: article.image_url || '',
      author: article.author || 'Equus Prime'
    });
    setShowArticleModal(true);
  };

  const openNewArticle = (dateKey = '') => {
    setEditingArticle(null);
    setArticleForm({ date_key: dateKey, title: '', content: '', body: '', image_url: '', author: 'Equus Prime' });
    setShowArticleModal(true);
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('Eliminare questo articolo?')) return;
    try {
      await axios.delete(`${API}/admin/result-articles/${articleId}`);
      fetchAllData();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleScheduleStaggered = async () => {
    if (!scheduleForm.firstClosing || scheduleForm.selectedAuctions.length === 0) {
      alert('Seleziona almeno un\'asta e imposta l\'orario della prima chiusura');
      return;
    }
    try {
      await axios.put(`${API}/admin/schedule-staggered`, {
        auction_ids: scheduleForm.selectedAuctions,
        first_closing: new Date(scheduleForm.firstClosing).toISOString(),
        interval_minutes: scheduleForm.intervalMinutes
      });
      alert(`${scheduleForm.selectedAuctions.length} aste programmate con successo!`);
      setShowScheduleModal(false);
      setScheduleForm({ firstClosing: '', intervalMinutes: 2, selectedAuctions: [] });
      fetchAllData();
    } catch (error) {
      alert('Errore durante la programmazione');
    }
  };

  const toggleAuctionSelection = (auctionId) => {
    setScheduleForm(prev => {
      const selected = prev.selectedAuctions.includes(auctionId)
        ? prev.selectedAuctions.filter(id => id !== auctionId)
        : [...prev.selectedAuctions, auctionId];
      return { ...prev, selectedAuctions: selected };
    });
  };

  const moveAuctionInSchedule = (index, direction) => {
    setScheduleForm(prev => {
      const newList = [...prev.selectedAuctions];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= newList.length) return prev;
      [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
      return { ...prev, selectedAuctions: newList };
    });
  };


  if (!user) return <Navigate to="/login" />;
  if (!user.is_admin) return <Navigate to="/" />;

  const tabs = [
    { id: 'users', label: 'Utenti', icon: '👥' },
    { id: 'horses', label: 'Cavalli', icon: '🐴' },
    { id: 'auctions', label: 'Aste', icon: '🔨' },
    { id: 'banners', label: 'Banner', icon: '📢' },
    { id: 'news', label: 'Notizie', icon: '📰' },
    { id: 'announcements', label: 'Prossime Aste', icon: '📅' },
    { id: 'results', label: 'Risultati', icon: '🏆' },
    { id: 'settings', label: 'Impostazioni', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#0a1628] text-white p-4">
        <div className="container-custom flex justify-between items-center">
          <h1 className="font-display text-2xl">Admin Dashboard</h1>
          <a href="/" className="text-[#c9a227] hover:underline">← Torna al sito</a>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-[#0a1628] text-white' 
                  : 'bg-white hover:bg-gray-50'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.icon} {tab.label}
              {tab.id === 'users' && pendingUsers.length > 0 && (
                <span className="ml-2 min-w-[20px] h-5 inline-flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1.5" data-testid="admin-tab-users-pending-badge">{pendingUsers.length}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <h2 className="font-display text-2xl">Gestione Utenti ({visibleUsers.length})</h2>
                  <div className="flex border border-[#0a1628]" data-testid="user-type-filter">
                    {[['all', 'Tutti'], ['privato', 'Privati'], ['azienda', 'Aziende']].map(([v, l]) => (
                      <button key={v} onClick={() => setUserTypeFilter(v)} className={`px-4 py-1.5 text-sm ${userTypeFilter === v ? 'bg-[#0a1628] text-white' : 'bg-white text-[#0a1628]'}`} data-testid={`user-type-filter-${v}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {pendingUsers.length > 0 && (
                  <button
                    onClick={() => setPendingOnly(!pendingOnly)}
                    className={`w-full text-left mb-6 p-4 border-l-4 border-red-500 transition-colors ${pendingOnly ? 'bg-red-100' : 'bg-red-50 hover:bg-red-100'}`}
                    data-testid="pending-users-alert"
                  >
                    <span className="font-bold text-[#0a1628]">Da verificare: {pendingUsers.length - pendingAziende} {pendingUsers.length - pendingAziende === 1 ? 'privato' : 'privati'}, {pendingAziende} {pendingAziende === 1 ? 'azienda' : 'aziende'}</span>
                    <span className="block text-sm text-[#64748b] mt-1">{pendingOnly ? 'Mostra tutti gli utenti' : 'Clicca per vedere solo chi aspetta la verifica'}</span>
                  </button>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="table-header">
                        <th className="text-left p-3">Nome</th>
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Telefono</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleUsers.map(u => (
                        <tr key={u.user_id} className="table-row">
                          <td className="p-3">
                            {u.name}
                            {u.is_super_admin ? (
                              <span className="ml-2 text-xs bg-[#0a1628] text-[#c9a227] px-2 py-1 whitespace-nowrap" data-testid={`role-badge-${u.user_id}`}>SUPER ADMIN</span>
                            ) : u.is_admin && <span className="ml-2 text-xs bg-[#c9a227] text-[#0a1628] px-2 py-1 whitespace-nowrap" data-testid={`role-badge-${u.user_id}`}>ADMIN</span>}
                            {u.account_type === 'azienda' && (
                              <>
                                <span className="ml-2 text-xs border border-[#0a1628] text-[#0a1628] px-2 py-0.5 whitespace-nowrap" data-testid={`company-badge-${u.user_id}`}>AZIENDA</span>
                                <div className="text-xs text-[#64748b] mt-1" data-testid={`company-info-${u.user_id}`}>
                                  {u.company_name} · P.IVA {u.vat_number}<br />{[u.address, u.postal_code, u.city].filter(Boolean).join(', ')}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="p-3">{u.email}</td>
                          <td className="p-3">{u.phone || '-'}</td>
                          <td className="p-3">
                            {u.is_suspended ? (
                              <span className="text-xs bg-red-600 text-white px-2 py-1 font-bold" data-testid={`suspended-badge-${u.user_id}`}>SOSPESO</span>
                            ) : u.is_verified ? (
                              <span className="verified-badge">Verificato</span>
                            ) : (
                              <span className="text-red-500 text-sm font-bold">Non verificato</span>
                            )}
                          </td>
                          <td className="p-3">
                            {canManage(u) ? (
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => toggleVerify(u.user_id, u.is_verified)}
                                  className={`px-4 py-1 text-sm ${u.is_verified ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}
                                  data-testid={`verify-user-${u.user_id}`}
                                >
                                  {u.is_verified ? 'Rimuovi verifica' : 'Verifica'}
                                </button>
                                <button
                                  onClick={() => (u.is_suspended || window.confirm(`Sospendere ${u.name}? Non potrà più accedere.`)) && updateUserFlag(u.user_id, 'suspend', !u.is_suspended)}
                                  className={`px-4 py-1 text-sm ${u.is_suspended ? 'bg-green-100 text-green-700' : 'bg-[#0a1628] text-white'}`}
                                  data-testid={`suspend-user-${u.user_id}`}
                                >
                                  {u.is_suspended ? 'Riattiva' : 'Sospendi'}
                                </button>
                                {user.is_super_admin && (
                                  <button
                                    onClick={() => window.confirm(u.is_admin ? `Togliere il ruolo Admin a ${u.name}?` : `Rendere ${u.name} Admin?`) && updateUserFlag(u.user_id, 'admin', !u.is_admin)}
                                    className="px-4 py-1 text-sm bg-[#c9a227]/20 text-[#0a1628]"
                                    data-testid={`toggle-admin-${u.user_id}`}
                                  >
                                    {u.is_admin ? 'Rimuovi Admin' : 'Rendi Admin'}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400" data-testid={`no-actions-${u.user_id}`}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Horses Tab */}
            {activeTab === 'horses' && (
              <div className="bg-white p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-display text-2xl">Gestione Cavalli ({horses.length})</h2>
                  <button onClick={() => setShowHorseModal(true)} className="btn-primary" data-testid="add-horse-btn">
                    + Aggiungi Cavallo
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {horses.map(horse => (
                    <div key={horse.horse_id} className="border border-gray-200 p-4">
                      {horse.images?.[0] && (
                        <img src={horse.images[0]} alt={horse.name} className="w-full h-40 object-cover mb-4" />
                      )}
                      <h3 className="font-bold text-lg">{horse.name}</h3>
                      <p className="text-gray-600 text-sm">{horse.breed} • {horse.age} anni • {normalizeGender(horse.gender)} • <span className="text-[#c9a227]">{typeLabel(horse.horse_type) || 'N/D'}</span></p>
                      <p className="text-gray-500 text-sm mt-2">{horse.location}</p>
                      <div className="flex gap-2 mt-4">
                        <button 
                          onClick={() => deleteHorse(horse.horse_id)}
                          className="px-4 py-1 bg-red-100 text-red-700 text-sm"
                          data-testid={`delete-horse-${horse.horse_id}`}
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Auctions Tab */}
            {activeTab === 'auctions' && (
              <div className="bg-white p-6">
                <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
                  <h2 className="font-display text-2xl">Gestione Aste ({auctions.length})</h2>
                  <div className="flex gap-2">
                    <button onClick={() => {
                      setScheduleForm({ firstClosing: '', intervalMinutes: 2, selectedAuctions: [] });
                      setShowScheduleModal(true);
                    }} className="px-4 py-2 bg-[#c9a227] text-white text-sm font-medium rounded hover:bg-[#b8911f] transition-colors" data-testid="schedule-staggered-btn">
                      Programma Asta Serale
                    </button>
                    <button onClick={() => setShowBulkAuctionModal(true)} className="px-4 py-2 bg-[#0a7e8c] text-white text-sm font-medium rounded hover:bg-[#086a76] transition-colors" data-testid="bulk-auction-btn">
                      Crea Aste Multiple
                    </button>
                    <button onClick={() => setShowAuctionModal(true)} className="btn-primary" data-testid="add-auction-btn">
                      + Crea Asta
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="table-header">
                        <th className="text-left p-3">Titolo</th>
                        <th className="text-left p-3">Prezzo Attuale</th>
                        <th className="text-left p-3">Offerte</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Fine</th>
                        <th className="text-left p-3">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auctions.map(auction => (
                        <tr key={auction.auction_id} className="table-row">
                          <td className="p-3 font-medium">{auction.title}</td>
                          <td className="p-3 text-[#c9a227] font-bold">€{auction.current_price?.toLocaleString()}</td>
                          <td className="p-3">{auction.total_bids}</td>
                          <td className="p-3">
                            <span className={`badge-${auction.status === 'active' ? 'live' : auction.status === 'scheduled' ? 'scheduled' : auction.status === 'draft' ? 'scheduled' : 'ended'}`}>
                              {auction.status === 'active' ? 'LIVE' : auction.status === 'scheduled' ? 'PROGRAMMATA' : auction.status === 'draft' ? 'BOZZA' : 'TERMINATA'}
                            </span>
                          </td>
                          <td className="p-3 text-sm">
                            {new Date(auction.end_time).toLocaleDateString('it-IT')}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {auction.status === 'scheduled' && (
                                <button
                                  onClick={() => updateAuctionStatus(auction.auction_id, 'active')}
                                  className="px-3 py-1 bg-green-100 text-green-700 text-sm"
                                >
                                  Attiva
                                </button>
                              )}
                              {auction.status === 'active' && (
                                <button
                                  onClick={() => updateAuctionStatus(auction.auction_id, 'ended')}
                                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm"
                                >
                                  Termina
                                </button>
                              )}
                              <button
                                onClick={() => deleteAuction(auction.auction_id)}
                                className="px-3 py-1 bg-red-100 text-red-700 text-sm"
                              >
                                Elimina
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Banners Tab */}
            {activeTab === 'banners' && (
              <div className="bg-white p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-display text-2xl">Gestione Banner ({banners.length})</h2>
                  <button onClick={() => setShowBannerModal(true)} className="btn-primary" data-testid="add-banner-btn">
                    + Aggiungi Banner
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {banners.map(banner => (
                    <div key={banner.banner_id} className="border border-gray-200 p-4">
                      <img src={banner.image_url} alt={banner.title} className="w-full h-32 object-cover mb-4" />
                      <h3 className="font-bold">{banner.title}</h3>
                      <p className="text-gray-600 text-sm">Posizione: {banner.position}</p>
                      <p className="text-gray-500 text-sm truncate">Link: {banner.link_url}</p>
                      <div className="flex gap-2 mt-4">
                        <span className={banner.is_active ? 'text-green-600' : 'text-red-600'}>
                          {banner.is_active ? '● Attivo' : '○ Inattivo'}
                        </span>
                        <button 
                          onClick={() => deleteBanner(banner.banner_id)}
                          className="px-4 py-1 bg-red-100 text-red-700 text-sm ml-auto"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* News Tab */}
            {activeTab === 'news' && (
              <div className="bg-white p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-display text-2xl">Gestione Notizie ({news.length})</h2>
                  <button onClick={() => setShowNewsModal(true)} className="btn-primary" data-testid="add-news-btn">
                    + Aggiungi Notizia
                  </button>
                </div>
                <div className="space-y-4">
                  {news.map(item => (
                    <div key={item.news_id} className="border border-gray-200 p-4 flex gap-4">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.title} className="w-32 h-24 object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <h3 className="font-bold">{item.title}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2">{item.excerpt}</p>
                        <p className="text-gray-400 text-xs mt-2">{item.author} • {new Date(item.created_at).toLocaleDateString('it-IT')}</p>
                      </div>
                      <button 
                        onClick={() => deleteNews(item.news_id)}
                        className="px-4 py-1 bg-red-100 text-red-700 text-sm h-fit"
                      >
                        Elimina
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Announcements Tab */}
            {activeTab === 'announcements' && (
              <div className="bg-white p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-display text-2xl">Prossime Aste ({announcements.length})</h2>
                  <button onClick={() => setShowAnnouncementModal(true)} className="btn-primary" data-testid="add-announcement-btn">
                    + Aggiungi Data
                  </button>
                </div>
                <p className="text-gray-500 text-sm mb-4">Gestisci le date delle prossime aste che appaiono nella homepage.</p>
                <div className="space-y-3">
                  {announcements.map(ann => (
                    <div key={ann.announcement_id} className="border border-gray-200 p-4 flex items-center justify-between rounded-lg" data-testid={`admin-ann-${ann.announcement_id}`}>
                      <div>
                        <p className="font-medium">{ann.text}</p>
                        <p className="text-gray-600 text-sm font-bold">{ann.time_text}</p>
                      </div>
                      <button 
                        onClick={() => deleteAnnouncement(ann.announcement_id)}
                        className="px-4 py-1 bg-red-100 text-red-700 text-sm"
                        data-testid={`delete-ann-${ann.announcement_id}`}
                      >
                        Elimina
                      </button>
                    </div>
                  ))}
                  {announcements.length === 0 && (
                    <p className="text-gray-400 text-center py-8">Nessuna data inserita. Clicca "+ Aggiungi Data" per aggiungere.</p>
                  )}
                </div>
              </div>
            )}

            {/* Results Tab */}
            {activeTab === 'results' && (
              <div className="space-y-6">
                {/* Result Articles - Custom articles for auction dates */}
                <div className="bg-white p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h2 className="font-display text-2xl">Articoli Risultati</h2>
                      <p className="text-gray-500 text-sm mt-1">Scrivi un articolo personalizzato per ogni data di asta conclusa. Apparirà nella pagina dettaglio risultati.</p>
                    </div>
                    <button onClick={() => openNewArticle()} className="btn-primary" data-testid="add-article-btn">
                      + Nuovo Articolo
                    </button>
                  </div>

                  {/* Ended auction dates without articles */}
                  {endedAuctionDates.filter(d => !resultArticles.find(a => a.date_key === d.date_key)).length > 0 && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm font-medium text-amber-800 mb-2">Date senza articolo:</p>
                      <div className="flex flex-wrap gap-2">
                        {endedAuctionDates
                          .filter(d => !resultArticles.find(a => a.date_key === d.date_key))
                          .map(d => (
                            <button
                              key={d.date_key}
                              onClick={() => openNewArticle(d.date_key)}
                              className="px-3 py-1.5 bg-white border border-amber-300 text-amber-800 text-sm rounded hover:bg-amber-100 transition-colors"
                              data-testid={`write-article-${d.date_key}`}
                            >
                              {new Date(d.date_key + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })} ({d.count} cavalli)
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  {/* Existing articles */}
                  <div className="space-y-3">
                    {resultArticles.map(article => (
                      <div key={article.article_id} className="border border-gray-200 p-4 rounded-lg" data-testid={`admin-article-${article.article_id}`}>
                        <div className="flex items-start gap-4">
                          {article.image_url && (
                            <img src={article.image_url} alt="" className="w-20 h-20 object-cover rounded flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs bg-[#0a1628] text-white px-2 py-0.5 rounded">
                                {new Date(article.date_key + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </span>
                              <span className="text-xs text-gray-400">di {article.author}</span>
                            </div>
                            <p className="font-medium text-[#0a1628]">{article.title}</p>
                            <p className="text-gray-500 text-sm mt-1 line-clamp-2">{article.content}</p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => openEditArticle(article)}
                              className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded hover:bg-blue-100"
                              data-testid={`edit-article-${article.article_id}`}
                            >
                              Modifica
                            </button>
                            <button
                              onClick={() => deleteArticle(article.article_id)}
                              className="px-3 py-1.5 bg-red-50 text-red-700 text-sm rounded hover:bg-red-100"
                              data-testid={`delete-article-${article.article_id}`}
                            >
                              Elimina
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {resultArticles.length === 0 && (
                      <p className="text-gray-400 text-center py-8">Nessun articolo scritto. Clicca "+ Nuovo Articolo" o seleziona una data sopra per scrivere.</p>
                    )}
                  </div>
                </div>

                {/* Legacy result cards for homepage */}
                <div className="bg-white p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h2 className="font-display text-xl">Card Risultati Homepage ({auctionResults.length})</h2>
                      <p className="text-gray-500 text-sm mt-1">Card manuali per la sezione risultati della homepage (se non ci sono aste concluse).</p>
                    </div>
                    <button onClick={() => setShowResultModal(true)} className="btn-primary text-sm" data-testid="add-result-btn">
                      + Aggiungi Card
                    </button>
                  </div>
                  <div className="space-y-3">
                    {auctionResults.map(result => (
                      <div key={result.result_id} className="border border-gray-200 p-4 flex items-center gap-4 rounded-lg" data-testid={`admin-result-${result.result_id}`}>
                        {result.image_url && (
                          <img src={result.image_url} alt={result.title} className="w-16 h-16 object-cover rounded flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{result.title}</p>
                          <p className="text-gray-500 text-sm">{result.date_text}</p>
                        </div>
                        <button 
                          onClick={() => deleteResult(result.result_id)}
                          className="px-4 py-1 bg-red-100 text-red-700 text-sm flex-shrink-0"
                          data-testid={`delete-result-${result.result_id}`}
                        >
                          Elimina
                        </button>
                      </div>
                    ))}
                    {auctionResults.length === 0 && (
                      <p className="text-gray-400 text-center py-4">Nessuna card risultato.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg">
                  <h2 className="font-display text-2xl mb-2">Impostazioni Sito</h2>
                  <p className="text-gray-500 text-sm mb-6">Gestisci le impostazioni generali del sito.</p>
                  
                  {/* Maintenance Mode Toggle */}
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-testid="maintenance-toggle-container">
                    <div>
                      <h3 className="font-medium text-[#0a1628]">Modalità Manutenzione</h3>
                      <p className="text-gray-500 text-sm mt-1">
                        Quando attiva, i visitatori vedranno una pagina "Sito in manutenzione". Tu come admin puoi continuare a usare il sito normalmente.
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const res = await axios.put(`${API}/admin/maintenance`);
                          setMaintenanceMode(res.data.maintenance);
                        } catch (err) {
                          console.error('Error toggling maintenance:', err);
                        }
                      }}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${maintenanceMode ? 'bg-red-500' : 'bg-gray-300'}`}
                      data-testid="maintenance-toggle-btn"
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${maintenanceMode ? 'translate-x-8' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {maintenanceMode && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm font-medium">Il sito è attualmente in manutenzione. I visitatori non possono accedere.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
      </div>

      {/* Horse Modal - Scheda Completa */}
      {showHorseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-xl">
            <h2 className="font-display text-2xl mb-6">Aggiungi Cavallo</h2>
            <form onSubmit={createHorse} className="space-y-4">
              {/* Info Base */}
              <p className="text-sm font-bold text-[#64748b] uppercase tracking-wider">Info Base</p>
              <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="Nome *" value={horseForm.name} onChange={e => setHorseForm({...horseForm, name: e.target.value})} className="input-field" required />
                <input type="text" placeholder="Razza *" value={horseForm.breed} onChange={e => setHorseForm({...horseForm, breed: e.target.value})} className="input-field" required />
                <input type="number" placeholder="Età *" value={horseForm.age} onChange={e => setHorseForm({...horseForm, age: e.target.value})} className="input-field" required />
                <input type="number" placeholder="Anno di nascita" value={horseForm.birth_year} onChange={e => setHorseForm({...horseForm, birth_year: e.target.value})} className="input-field" />
                <select value={horseForm.gender} onChange={e => setHorseForm({...horseForm, gender: e.target.value})} className="input-field" data-testid="horse-gender-select">
                  {GENDERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={horseForm.horse_type} onChange={e => setHorseForm({...horseForm, horse_type: e.target.value})} className="input-field" data-testid="horse-type-select">
                  {HORSE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={horseForm.galoppo_level} onChange={e => setHorseForm({...horseForm, galoppo_level: e.target.value})} className="input-field" data-testid="horse-galoppo-select">
                  <option value="">Galoppo (non indicato)</option>
                  {GALOPPO_LEVELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input type="text" placeholder="Colore *" value={horseForm.color} onChange={e => setHorseForm({...horseForm, color: e.target.value})} className="input-field" required />
                <input type="number" placeholder="Altezza (cm)" value={horseForm.height_cm} onChange={e => setHorseForm({...horseForm, height_cm: e.target.value})} className="input-field" />
              </div>
              <input type="text" placeholder="Ubicazione *" value={horseForm.location} onChange={e => setHorseForm({...horseForm, location: e.target.value})} className="input-field" required />
              
              {/* Foto */}
              <ImageUploader
                value={horseForm.images}
                onChange={(imgs) => setHorseForm({...horseForm, images: imgs})}
                label="Foto del cavallo"
                multiple={true}
                testId="horse-images-uploader"
              />
              
              {/* Descrizione */}
              <p className="text-sm font-bold text-[#64748b] uppercase tracking-wider pt-2">Descrizione</p>
              <textarea placeholder="Descrizione completa del cavallo *" value={horseForm.description} onChange={e => setHorseForm({...horseForm, description: e.target.value})} className="input-field" rows={4} required />
              
              {/* Linea di Sangue */}
              <p className="text-sm font-bold text-[#64748b] uppercase tracking-wider pt-2">Linea di Sangue</p>
              <div className="grid grid-cols-3 gap-4">
                <input type="text" placeholder="Padre" value={horseForm.father} onChange={e => setHorseForm({...horseForm, father: e.target.value})} className="input-field" />
                <input type="text" placeholder="Madre" value={horseForm.mother} onChange={e => setHorseForm({...horseForm, mother: e.target.value})} className="input-field" />
                <input type="text" placeholder="Nonno materno" value={horseForm.grandfather} onChange={e => setHorseForm({...horseForm, grandfather: e.target.value})} className="input-field" />
              </div>
              <input type="text" placeholder="N. Registrazione" value={horseForm.registration_number} onChange={e => setHorseForm({...horseForm, registration_number: e.target.value})} className="input-field" />

              {/* Gravidanza (solo per fattrici) */}
              <p className="text-sm font-bold text-[#64748b] uppercase tracking-wider pt-2">Gravidanza (se fattrice)</p>
              <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="Gravida con (nome stallone)" value={horseForm.pregnant_with} onChange={e => setHorseForm({...horseForm, pregnant_with: e.target.value})} className="input-field" />
                <input type="text" placeholder="Data parto prevista" value={horseForm.expected_foaling} onChange={e => setHorseForm({...horseForm, expected_foaling: e.target.value})} className="input-field" />
              </div>

              {/* Documenti */}
              <div className="pt-2">
                <DocUploader docs={horseDocs} setDocs={setHorseDocs} />
              </div>

              {/* Contatto Venditore */}
              <p className="text-sm font-bold text-[#64748b] uppercase tracking-wider pt-2">Contatto Venditore</p>
              <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="Nome venditore/scuderia" value={horseForm.seller_name} onChange={e => setHorseForm({...horseForm, seller_name: e.target.value})} className="input-field" />
                <input type="text" placeholder="Persona di contatto" value={horseForm.seller_contact} onChange={e => setHorseForm({...horseForm, seller_contact: e.target.value})} className="input-field" />
                <input type="email" placeholder="Email venditore" value={horseForm.seller_email} onChange={e => setHorseForm({...horseForm, seller_email: e.target.value})} className="input-field" />
                <input type="tel" placeholder="Telefono venditore" value={horseForm.seller_phone} onChange={e => setHorseForm({...horseForm, seller_phone: e.target.value})} className="input-field" />
              </div>

              {/* Link Esterno */}
              <p className="text-sm font-bold text-[#64748b] uppercase tracking-wider pt-2">Link Esterno</p>
              <div className="grid grid-cols-2 gap-4">
                <input type="url" placeholder="URL link esterno" value={horseForm.external_link} onChange={e => setHorseForm({...horseForm, external_link: e.target.value})} className="input-field" />
                <input type="text" placeholder="Etichetta link (es: ANAGT)" value={horseForm.external_link_label} onChange={e => setHorseForm({...horseForm, external_link_label: e.target.value})} className="input-field" />
              </div>

              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => { setShowHorseModal(false); setHorseDocs([]); }} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" className="btn-primary flex-1">Salva Cavallo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auction Modal */}
      {showAuctionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-lg w-full">
            <h2 className="font-display text-2xl mb-6">Crea Asta</h2>
            <form onSubmit={createAuction} className="space-y-4">
              <select value={auctionForm.horse_id} onChange={e => setAuctionForm({...auctionForm, horse_id: e.target.value})} className="input-field" required>
                <option value="">Seleziona cavallo *</option>
                {horses.map(h => (
                  <option key={h.horse_id} value={h.horse_id}>{h.name} ({h.breed})</option>
                ))}
              </select>
              <input type="text" placeholder="Titolo asta *" value={auctionForm.title} onChange={e => setAuctionForm({...auctionForm, title: e.target.value})} className="input-field" required />
              <input type="number" placeholder="Prezzo partenza (€)" value={auctionForm.starting_price} onChange={e => setAuctionForm({...auctionForm, starting_price: e.target.value})} className="input-field" min={500} />
              <div>
                <label className="block text-sm mb-1">Data/ora inizio *</label>
                <input type="datetime-local" value={auctionForm.start_time} onChange={e => setAuctionForm({...auctionForm, start_time: e.target.value})} className="input-field" required />
              </div>
              <div>
                <label className="block text-sm mb-1">Data/ora fine *</label>
                <input type="datetime-local" value={auctionForm.end_time} onChange={e => setAuctionForm({...auctionForm, end_time: e.target.value})} className="input-field" required />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowAuctionModal(false)} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" className="btn-primary flex-1">Crea Asta</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Auction Modal */}
      {showBulkAuctionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-auction-modal">
          <div className="bg-white p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg">
            <h2 className="font-display text-2xl mb-2">Crea Aste Multiple</h2>
            <p className="text-sm text-[#64748b] mb-6">Seleziona i cavalli e imposta orario e prezzo. Le aste partono subito!</p>
            
            <form onSubmit={createBulkAuctions}>
              {/* Horse Selection */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium text-sm">Seleziona cavalli ({bulkSelectedHorses.length}/{horses.length})</p>
                  <button 
                    type="button" 
                    onClick={toggleAllHorses}
                    className="text-sm text-[#0a7e8c] font-medium hover:underline"
                    data-testid="bulk-select-all"
                  >
                    {bulkSelectedHorses.length === horses.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                  {horses.map(horse => (
                    <label 
                      key={horse.horse_id} 
                      className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${bulkSelectedHorses.includes(horse.horse_id) ? 'bg-[#0a7e8c]/5' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={bulkSelectedHorses.includes(horse.horse_id)}
                        onChange={() => toggleBulkHorse(horse.horse_id)}
                        className="w-4 h-4 accent-[#0a7e8c]"
                        data-testid={`bulk-horse-${horse.horse_id}`}
                      />
                      {horse.images?.[0] && (
                        <img src={horse.images[0]} alt="" className="w-10 h-10 rounded object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{horse.name}</p>
                        <p className="text-xs text-[#64748b]">{horse.breed} - {normalizeGender(horse.gender)}</p>
                      </div>
                    </label>
                  ))}
                  {horses.length === 0 && (
                    <p className="p-4 text-center text-[#64748b] text-sm">Nessun cavallo disponibile. Creane uno nella tab "Cavalli".</p>
                  )}
                </div>
              </div>

              {/* Shared Settings */}
              <div className="space-y-4 mb-6">
                <input type="number" placeholder="Prezzo partenza per tutti (€)" value={bulkForm.starting_price} onChange={e => setBulkForm({...bulkForm, starting_price: e.target.value})} className="input-field" min={500} required data-testid="bulk-price" />
                <p className="text-sm text-gray-500">Le aste verranno create come <strong>Bozza</strong>. Usa "Programma Asta Serale" per renderle visibili con gli orari scalari.</p>
              </div>

              <div className="flex gap-4">
                <button type="button" onClick={() => { setShowBulkAuctionModal(false); setBulkSelectedHorses([]); }} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" disabled={bulkCreating || bulkSelectedHorses.length === 0} className="flex-1 bg-[#0a7e8c] text-white py-2 rounded font-bold hover:bg-[#086a76] disabled:opacity-50 transition-colors" data-testid="bulk-create-btn">
                  {bulkCreating ? 'Creazione...' : `Crea ${bulkSelectedHorses.length} Aste`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Banner Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-lg w-full">
            <h2 className="font-display text-2xl mb-6">Aggiungi Banner</h2>
            <form onSubmit={createBanner} className="space-y-4">
              <input type="text" placeholder="Titolo *" value={bannerForm.title} onChange={e => setBannerForm({...bannerForm, title: e.target.value})} className="input-field" required />
              <ImageUploader
                value={bannerForm.image_url}
                onChange={(url) => setBannerForm({...bannerForm, image_url: url})}
                label="Immagine banner *"
                testId="banner-image-uploader"
              />
              <input type="url" placeholder="URL Link *" value={bannerForm.link_url} onChange={e => setBannerForm({...bannerForm, link_url: e.target.value})} className="input-field" required />
              <select value={bannerForm.position} onChange={e => setBannerForm({...bannerForm, position: e.target.value})} className="input-field">
                <option value="top">Top (sopra navbar)</option>
                <option value="header">Header (in pagina)</option>
                <option value="sidebar">Sidebar</option>
                <option value="between_auctions">Tra le aste</option>
              </select>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={bannerForm.is_active} onChange={e => setBannerForm({...bannerForm, is_active: e.target.checked})} />
                Attivo
              </label>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowBannerModal(false)} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" className="btn-primary flex-1">Salva Banner</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* News Modal */}
      {showNewsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-xl">
            <h2 className="font-display text-2xl mb-6">Aggiungi Articolo Asta</h2>
            <form onSubmit={createNews} className="space-y-4">
              <input type="text" placeholder="Titolo * (es: Fulmine Dorato il più costoso della serata)" value={newsForm.title} onChange={e => setNewsForm({...newsForm, title: e.target.value})} className="input-field" required />
              <input type="text" placeholder="Estratto * (breve descrizione per l'anteprima)" value={newsForm.excerpt} onChange={e => setNewsForm({...newsForm, excerpt: e.target.value})} className="input-field" required />
              <ImageUploader
                value={newsForm.image_url}
                onChange={(url) => setNewsForm({...newsForm, image_url: url})}
                label="Immagine principale"
                testId="news-image-uploader"
              />
              <input type="text" placeholder="Autore" value={newsForm.author} onChange={e => setNewsForm({...newsForm, author: e.target.value})} className="input-field" />
              <textarea placeholder="Contenuto completo dell'articolo *" value={newsForm.content} onChange={e => setNewsForm({...newsForm, content: e.target.value})} className="input-field" rows={6} required />
              
              {/* Results Table Builder */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-sm">Tabella Risultati (opzionale)</h3>
                  <button type="button" onClick={addTableRow} className="text-sm text-[#c9a227] font-medium hover:underline" data-testid="add-table-row-btn">
                    + Aggiungi riga
                  </button>
                </div>
                {tableRows.length > 0 && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-xs font-medium text-[#64748b]">
                      <span>Cavallo</span><span>Tipo</span><span>Compratore</span><span>Prezzo</span><span></span>
                    </div>
                    {tableRows.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2">
                        <input type="text" placeholder="Nome cavallo" value={row.horse} onChange={e => updateTableRow(idx, 'horse', e.target.value)} className="input-field text-sm py-1.5" />
                        <input type="text" placeholder="Tipo" value={row.type} onChange={e => updateTableRow(idx, 'type', e.target.value)} className="input-field text-sm py-1.5" />
                        <input type="text" placeholder="Compratore" value={row.buyer} onChange={e => updateTableRow(idx, 'buyer', e.target.value)} className="input-field text-sm py-1.5" />
                        <input type="text" placeholder="Prezzo" value={row.price} onChange={e => updateTableRow(idx, 'price', e.target.value)} className="input-field text-sm py-1.5" />
                        <button type="button" onClick={() => removeTableRow(idx)} className="text-red-500 text-sm px-2 hover:text-red-700">✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {tableRows.length === 0 && (
                  <p className="text-gray-400 text-xs">Nessuna riga. Clicca "+ Aggiungi riga" per inserire i risultati dell'asta.</p>
                )}
              </div>

              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newsForm.is_published} onChange={e => setNewsForm({...newsForm, is_published: e.target.checked})} />
                Pubblica immediatamente
              </label>
              <div className="flex gap-4">
                <button type="button" onClick={() => { setShowNewsModal(false); setTableRows([]); }} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" className="btn-primary flex-1">Pubblica</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-lg w-full rounded-xl">
            <h2 className="font-display text-2xl mb-6">Aggiungi Data Asta</h2>
            <form onSubmit={handleAnnouncementSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Testo annuncio *</label>
                <input type="text" placeholder="Es: Asta di sabato 29 marzo" value={announcementForm.text} onChange={e => setAnnouncementForm({...announcementForm, text: e.target.value})} className="input-field" required data-testid="announcement-text-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Orario *</label>
                <input type="text" placeholder="Es: alle ore 19:15." value={announcementForm.time_text} onChange={e => setAnnouncementForm({...announcementForm, time_text: e.target.value})} className="input-field" required data-testid="announcement-time-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ordine (numero)</label>
                <input type="number" placeholder="0" value={announcementForm.order} onChange={e => setAnnouncementForm({...announcementForm, order: e.target.value})} className="input-field" />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowAnnouncementModal(false)} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" className="btn-primary flex-1" data-testid="announcement-submit-btn">Aggiungi</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auction Result Modal */}
      {showResultModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-lg w-full rounded-xl">
            <h2 className="font-display text-2xl mb-6">Aggiungi Risultato Asta</h2>
            <form onSubmit={handleResultSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titolo *</label>
                <input type="text" placeholder="Es: Fulmine Dorato aggiudicato per 12.500 EUR" value={resultForm.title} onChange={e => setResultForm({...resultForm, title: e.target.value})} className="input-field" required data-testid="result-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data *</label>
                <input type="text" placeholder="Es: 14 marzo 2026" value={resultForm.date_text} onChange={e => setResultForm({...resultForm, date_text: e.target.value})} className="input-field" required data-testid="result-date-input" />
              </div>
              <ImageUploader
                value={resultForm.image_url}
                onChange={(url) => setResultForm({...resultForm, image_url: url})}
                label="Foto Cavallo"
                testId="result-image-uploader"
              />
              <div>
                <label className="block text-sm font-medium mb-1">Link (opzionale)</label>
                <input type="text" placeholder="/risultati" value={resultForm.link_url} onChange={e => setResultForm({...resultForm, link_url: e.target.value})} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ordine</label>
                <input type="number" placeholder="0" value={resultForm.order} onChange={e => setResultForm({...resultForm, order: e.target.value})} className="input-field" />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowResultModal(false)} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" className="btn-primary flex-1" data-testid="result-submit-btn">Aggiungi</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Result Article Modal */}
      {showArticleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-xl">
            <h2 className="font-display text-2xl mb-6">{editingArticle ? 'Modifica Articolo' : 'Nuovo Articolo Risultati'}</h2>
            <form onSubmit={handleArticleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Data Asta *</label>
                {editingArticle ? (
                  <p className="text-[#0a1628] font-medium">{new Date(articleForm.date_key + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                ) : (
                  <select
                    value={articleForm.date_key}
                    onChange={e => setArticleForm({...articleForm, date_key: e.target.value})}
                    className="input-field"
                    required
                    data-testid="article-date-select"
                  >
                    <option value="">Seleziona data...</option>
                    {endedAuctionDates.map(d => (
                      <option key={d.date_key} value={d.date_key} disabled={!!resultArticles.find(a => a.date_key === d.date_key)}>
                        {new Date(d.date_key + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })} ({d.count} cavalli)
                        {resultArticles.find(a => a.date_key === d.date_key) ? ' - Articolo esistente' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titolo Articolo *</label>
                <input
                  type="text"
                  placeholder="Es: Aurora Stellare domina l'asta della serata"
                  value={articleForm.title}
                  onChange={e => setArticleForm({...articleForm, title: e.target.value})}
                  className="input-field"
                  required
                  data-testid="article-title-input"
                />
              </div>
              <ImageUploader
                value={articleForm.image_url}
                onChange={(url) => setArticleForm({...articleForm, image_url: url})}
                label="Immagine principale dell'articolo"
                testId="article-image-uploader"
              />
              <div>
                <label className="block text-sm font-medium mb-1">Autore</label>
                <input
                  type="text"
                  placeholder="Equus Prime"
                  value={articleForm.author}
                  onChange={e => setArticleForm({...articleForm, author: e.target.value})}
                  className="input-field"
                  data-testid="article-author-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Testo introduttivo (hero) *</label>
                <textarea
                  placeholder="Breve introduzione che appare nella sezione hero scura..."
                  value={articleForm.content}
                  onChange={e => setArticleForm({...articleForm, content: e.target.value})}
                  className="input-field"
                  rows={4}
                  required
                  data-testid="article-content-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Corpo dell'articolo (sotto la foto)</label>
                <p className="text-xs text-gray-400 mb-2">Testo esteso che appare sotto la foto. Usa **testo** per il grassetto. Separa i paragrafi con una riga vuota.</p>
                <textarea
                  placeholder={"Esempio:\n\n**Aurora Stellare** ha dominato la serata con un prezzo finale di 12.500 EUR...\n\n**Tornado Nero**, che è stato acquistato da...\n\nTravera torna sabato!"}
                  value={articleForm.body}
                  onChange={e => setArticleForm({...articleForm, body: e.target.value})}
                  className="input-field font-mono text-sm"
                  rows={10}
                  data-testid="article-body-input"
                />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => { setShowArticleModal(false); setEditingArticle(null); }} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" className="btn-primary flex-1" data-testid="article-submit-btn">
                  {editingArticle ? 'Salva Modifiche' : 'Pubblica Articolo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Staggered Auction Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="font-display text-2xl mb-2" data-testid="schedule-modal-title">Programma Asta Serale</h2>
            <p className="text-gray-500 text-sm mb-6">Imposta l'orario della prima chiusura e l'intervallo tra ogni cavallo. Come Travera, ogni cavallo chiuderà in sequenza.</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Prima Chiusura</label>
                <input
                  type="datetime-local"
                  value={scheduleForm.firstClosing}
                  onChange={e => setScheduleForm({...scheduleForm, firstClosing: e.target.value})}
                  className="input-field"
                  data-testid="schedule-first-closing"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Intervallo (minuti)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={scheduleForm.intervalMinutes}
                  onChange={e => setScheduleForm({...scheduleForm, intervalMinutes: parseInt(e.target.value) || 2})}
                  className="input-field"
                  data-testid="schedule-interval"
                />
              </div>
            </div>

            {/* Preview of schedule */}
            {scheduleForm.firstClosing && scheduleForm.selectedAuctions.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-sm mb-2">Anteprima orari chiusura:</h4>
                {scheduleForm.selectedAuctions.map((id, i) => {
                  const auction = auctions.find(a => a.auction_id === id);
                  const closeTime = new Date(new Date(scheduleForm.firstClosing).getTime() + i * scheduleForm.intervalMinutes * 60000);
                  return (
                    <div key={id} className="flex items-center justify-between text-sm py-1 border-b border-gray-200 last:border-0">
                      <span className="font-medium">{i + 1}. {auction?.title || id}</span>
                      <span className="text-[#c9a227] font-bold">{closeTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mb-4">
              <h4 className="font-medium text-sm mb-2">Seleziona e ordina i cavalli (clicca per selezionare, frecce per riordinare):</h4>
            </div>
            
            {/* Selected auctions with reorder */}
            {scheduleForm.selectedAuctions.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs uppercase tracking-wider text-[#c9a227] font-medium mb-2">Ordine chiusura ({scheduleForm.selectedAuctions.length} selezionate):</h4>
                <div className="space-y-1">
                  {scheduleForm.selectedAuctions.map((id, i) => {
                    const auction = auctions.find(a => a.auction_id === id);
                    return (
                      <div key={id} className="flex items-center gap-2 bg-[#c9a227]/10 border border-[#c9a227]/30 px-3 py-2 rounded">
                        <span className="font-bold text-[#c9a227] w-6">{i + 1}.</span>
                        <span className="flex-1 text-sm font-medium">{auction?.title || id}</span>
                        <button onClick={() => moveAuctionInSchedule(i, -1)} disabled={i === 0} className="px-2 py-0.5 text-xs bg-gray-200 rounded disabled:opacity-30">↑</button>
                        <button onClick={() => moveAuctionInSchedule(i, 1)} disabled={i === scheduleForm.selectedAuctions.length - 1} className="px-2 py-0.5 text-xs bg-gray-200 rounded disabled:opacity-30">↓</button>
                        <button onClick={() => toggleAuctionSelection(id)} className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded">✕</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Available auctions to add */}
            <div className="space-y-1 mb-6">
              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Aste disponibili:</h4>
              {auctions
                .filter(a => a.status === 'active' || a.status === 'scheduled' || a.status === 'draft')
                .filter(a => !scheduleForm.selectedAuctions.includes(a.auction_id))
                .map(auction => (
                  <button
                    key={auction.auction_id}
                    onClick={() => toggleAuctionSelection(auction.auction_id)}
                    className="w-full flex items-center gap-3 px-3 py-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors text-left"
                    data-testid={`schedule-select-${auction.auction_id}`}
                  >
                    <span className="w-5 h-5 border-2 border-gray-300 rounded flex items-center justify-center text-xs">+</span>
                    <span className="text-sm font-medium">{auction.title}</span>
                    <span className="text-xs text-gray-400 ml-auto">€{auction.current_price?.toLocaleString()}</span>
                  </button>
                ))}
            </div>

            <div className="flex gap-4">
              <button onClick={() => setShowScheduleModal(false)} className="btn-secondary flex-1">Annulla</button>
              <button 
                onClick={handleScheduleStaggered} 
                className="btn-primary flex-1"
                disabled={scheduleForm.selectedAuctions.length === 0 || !scheduleForm.firstClosing}
                data-testid="schedule-confirm-btn"
              >
                Programma {scheduleForm.selectedAuctions.length} Aste
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
